<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import FaqService from '@/services/faq/faqService.ts';
import { displayToast } from '@/utils/toastUtils.ts';

import type { FaqRequest, FaqResponse } from '@/types/services/faq/faq.type.ts';

const QUESTION_MAX_LENGTH = 300;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const faqId = computed<number | null>(() => (route.params.id ? Number(route.params.id) : null));
const isEdit = computed<boolean>(() => faqId.value !== null);

const loading = ref<boolean>(false);
const formValid = ref<boolean>(false);

const form = reactive<{ question: string; answer: string; isPublished: boolean }>({
  question: '',
  answer: '',
  isPublished: false,
});

const questionRules = [
  (value: string): boolean | string => !!value?.trim() || t('views.faq.form.required'),
  (value: string): boolean | string => (value ?? '').length <= QUESTION_MAX_LENGTH || t('views.faq.form.question.hint'),
];

// Quill leaves "<p><br></p>" when the editor is emptied
const answerIsEmpty = computed<boolean>(() => form.answer.replace(/<[^>]*>/g, '').trim() === '');

onMounted(async () => {
  if (faqId.value === null) return;

  loading.value = true;
  try {
    const faq: FaqResponse = await FaqService.getById(faqId.value);
    form.question = faq.question;
    form.answer = faq.answer;
    form.isPublished = faq.is_published;
  } catch {
    await router.replace({ name: 'faqList' });
  } finally {
    loading.value = false;
  }
});

async function submit(): Promise<void> {
  if (!formValid.value || answerIsEmpty.value) return;

  const payload: FaqRequest = {
    question: form.question.trim(),
    answer: form.answer,
    isPublished: form.isPublished,
  };

  loading.value = true;
  try {
    if (faqId.value === null) {
      await FaqService.create(payload);
      displayToast(t('views.faq.form.createSuccess'), 'success');
    } else {
      await FaqService.update(faqId.value, payload);
      displayToast(t('views.faq.form.updateSuccess'), 'success');
    }
    await router.push({ name: 'faqList' });
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <MainTitleComponent>
    {{ isEdit ? t('views.faq.form.editTitle') : t('views.faq.form.createTitle') }}
  </MainTitleComponent>

  <v-form v-model="formValid" class="mt-4" @submit.prevent="submit">
    <v-row>
      <v-col cols="12" md="8">
        <v-text-field
          v-model="form.question"
          :counter="QUESTION_MAX_LENGTH"
          :disabled="loading"
          :hint="t('views.faq.form.question.hint')"
          :label="t('views.faq.form.question.label')"
          :rules="questionRules"
          hide-details="auto"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="8">
        <p class="mb-2">{{ t('views.faq.form.answer.label') }}</p>
        <QuillEditor
          v-model:content="form.answer"
          :placeholder="t('views.faq.form.answer.placeholder')"
          :read-only="loading"
          :toolbar="[
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ header: [2, 3, false] }],
            ['link', 'blockquote'],
            ['clean']
          ]"
          content-type="html"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="8">
        <v-switch
          v-model="form.isPublished"
          :disabled="loading"
          :label="t('views.faq.form.published.label')"
          color="primary"
          hide-details
          inset
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col class="d-flex ga-4" cols="12" md="8">
        <v-btn variant="outlined" @click="router.push({ name: 'faqList' })">
          {{ t('common.buttons.cancel') }}
        </v-btn>
        <v-btn
          :disabled="!formValid || answerIsEmpty || loading"
          :loading="loading"
          color="primary"
          type="submit"
          variant="flat"
        >
          {{ isEdit ? t('common.buttons.update') : t('common.buttons.create') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>
